import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useToast } from '@/design-system';
import { ApiError, isNonRetryable, toUserMessage } from '@/lib/api';

/**
 * Cliente TanStack Query.
 *
 * Decisões:
 *  · `retry` NÃO repete 4xx. Repetir um 403 ou um 422 só gera ruído na trilha
 *    de auditoria — e repetir um ato assinado seria pior: a prova de assinatura
 *    é de uso único, a segunda tentativa falharia com PROOF_ALREADY_USED e
 *    confundiria o usuário sobre o que de fato foi registrado.
 *  · mutação NUNCA tem retry automático. Todo ato de escrita neste sistema é
 *    auditado; reexecutar sozinho poderia duplicar registro.
 *  · `staleTime` curto (30 s): dado regulado desatualizado na tela é pior do
 *    que uma requisição a mais.
 *  · erro de mutação vira toast automaticamente, com a mensagem já traduzida
 *    do `ErrorCode`; quem precisa de tratamento específico sobrescreve com
 *    `onError` local (é o caso do diálogo de assinatura).
 */
export function QueryProvider({ children }: { readonly children: ReactNode }) {
  const { toast } = useToast();

  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            retry: (failureCount, error) => {
              if (isNonRetryable(error)) return false;
              return failureCount < 2;
            },
            retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
          },
          mutations: {
            retry: false,
          },
        },

        queryCache: new QueryCache({
          onError: (error, query) => {
            // Só avisa em falha de refetch de dado já exibido; a primeira carga
            // é tratada pela própria tela, com estado de erro no lugar do
            // conteúdo (mais informativo que um toast).
            if (query.state.data === undefined) return;
            if (error instanceof ApiError && error.status === 401) return;
            toast({
              tone: 'warning',
              title: 'Não foi possível atualizar os dados',
              description: toUserMessage(error),
            });
          },
        }),

        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (mutation.options.onError) return; // tratamento específico assume
            if (error instanceof ApiError && error.status === 401) return;
            toast({
              tone: 'error',
              title: 'A operação não foi concluída',
              description: toUserMessage(error),
            });
          },
        }),
      }),
  );

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
